// Greenhold site footer — trader identification + legal document links.
//
// Story 6.10 / action item 16. Shared by BOTH apps (the React SPA shell and
// the Astro public site) so the company details and the legal links can
// never drift apart. Uses plain <a> rather than a router Link precisely so
// it drops into either app unchanged; a full navigation to a legal page is
// fine (and arguably correct — these are documents, not app surfaces).
//
// The 18+ line is deliberate: session item E11 settled the gambling/loot-box
// posture as "18+ as a contractual self-declaration, no age-verification
// infrastructure at v1". Stating it plainly in the chrome is part of that
// posture holding up.
//
// The geographic address is deliberately NOT rendered (owner request
// 2026-08-15): the registered address is the founder's home. It remains in
// the served legal documents — linked one line above — which keeps the
// trader identification available without printing a private address into
// every page of the site.

import { cn } from "../lib/utils";
import { LEGAL_ENTITY } from "./legalEntity";

export type SiteFooterProps = {
  /** Extra classes for the outer <footer>. Composed with `cn()` (i.e.
   *  tailwind-merge), so a caller CAN override the defaults — notably the
   *  `mt-16` breathing room, which is right above a light page ground
   *  (the public site) but reads as dead space above a forest-900 one
   *  (the SPA shell, where the gap and the footer are the same colour and
   *  the gold hairline ends up floating in an unexplained dark band).
   *  Pass `className="mt-0"` on dark grounds. */
  className?: string;
};

const LINKS = [
  { href: "/legal/terms", label: "Terms of Service" },
  { href: "/legal/creator-program", label: "Creator Program Terms" },
  { href: "/legal/privacy", label: "Privacy Policy" },
];

export function SiteFooter({ className }: SiteFooterProps) {
  return (
    <footer
      data-testid="site-footer"
      className={cn(
        "mt-16 border-t border-brand-gold-500/20 bg-brand-forest-900 text-brand-paper-100",
        className,
      )}
    >
      <div className="mx-auto w-full max-w-6xl px-6 py-8 space-y-4">
        <nav
          aria-label="Legal"
          className="flex flex-wrap items-center gap-x-6 gap-y-2 font-body text-sm"
        >
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-brand-gold-300 underline-offset-4 hover:underline"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="space-y-1 font-mono text-[0.7rem] leading-relaxed opacity-60">
          <p>
            {LEGAL_ENTITY.name} · CVR {LEGAL_ENTITY.cvr}
          </p>
          <p>
            <a
              href={`mailto:${LEGAL_ENTITY.email}`}
              className="underline-offset-4 hover:underline"
            >
              {LEGAL_ENTITY.email}
            </a>
          </p>
          <p>Greenhold is for users aged 18 and over.</p>
        </div>
      </div>
    </footer>
  );
}
